import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IDeleteExpenseInPayrollForEmployeeRequestParams } from "../../domain/IDeleteExpenseInPayrollForEmployeeRequestParams";
import { IDeletePayrollForEmployeeRequestParams } from "../../domain/IDeletePayrollForEmployeeRequestParams";
import { IExpense } from "../../domain/IExpense";
import { IPayroll } from "../../domain/IPayroll";
import { hrService } from "../../services/hr.services";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IGetCarnetsRequestParams } from "Dashboard/scenes/Carnets/domain/IGetCarnetsRequestParams";
import { ICarnet } from "Dashboard/scenes/Carnets/domain/ICarnet";
import { ICarentsTotals } from "Dashboard/scenes/Carnets/domain/ICarnetsTotals";
import { ISinglePayrollForPrint } from "../../domain/ISinglePayrollForPrint";
import { IGetPayrollsForPrintRequestParams } from "../../domain/IGetPayrollsForPrintRequestParams";
import { IUpdatePayrollForPrintParams } from "../../domain/IUpdatePayrollForPrintParams";

export const getAllPayrollsForEmployee = createAsyncThunk(
  "HumanResources/getPayrolls",
  async (employeeId: number) => {
    try {
      return await hrService.getAllPayrollsForEmployee(employeeId);
    } catch (err) {
      setError(err);
    }
  }
);

export const getAllPayrollsForEmployeePrint = createAsyncThunk(
  "HumanResources/getPayrollsForPrint",
  async (data: IGetPayrollsForPrintRequestParams) => {
    try {
      return await hrService.getAllPayrollsForPrint(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getMonthlyPayrollForEmployeePrint = createAsyncThunk(
  "HumanResources/getPayrollForPrint",
  async (data: IGetPayrollsForPrintRequestParams) => {
    try {
      return await hrService.getAllPayrollsForPrint(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const getMonthlyCarnetsForEmployee = createAsyncThunk(
  "HumanResources/getMonthlyCarnetsForEmployee",
  async (data: IGetCarnetsRequestParams) => {
    try {
      return await hrService.getCarnetsInPeriod(data);
    } catch (err) {
      setError(err);
    }
  }
);

export const deletePayroll = createAsyncThunk(
  "HumanResources/deletePayroll",
  async (params: IDeletePayrollForEmployeeRequestParams) => {
    try {
      const response = await hrService.deletePayrollForEmployee(
        params.employeeId,
        params.payrollId
      );
      return {
        response: response,
        params: params,
      }
    } catch (err) {
      setError(err);
    }
  }
);

export const deleteExpenseInPayrollForEmployee = createAsyncThunk(
  "HumanResources/deleteExpenseInPayrollForEmployee",
  async (params: IDeleteExpenseInPayrollForEmployeeRequestParams) => {
    try {
      return await hrService.deleteExpenseInPayrollForEmployee(params);
    } catch (err) {
      setError(err);
    }
  }
);
interface payrollState {
  payrolls: IPayroll[];
  payrollsForPrint: ISinglePayrollForPrint[];
  payrollForPrint: ISinglePayrollForPrint;
  carnets: ICarnet[];
  totals: ICarentsTotals;
  loading: boolean;
  error: any;
  carnetsLoading: boolean;
  calculationPrintModalOpen: boolean;
  calculationPopoverOpen: IPopoverState;
  calculationAllPayrollsConfirmationOpen: boolean;
  indexForDeletion: number;
  calculatePayrollPopover: IPopoverState;
  deletePayrollModalOpen: boolean;
}

const initialState: payrollState = {
  payrolls: [],
  payrollsForPrint: [],
  payrollForPrint: {
    employeeName: "",
    role: "",
    year: 0,
    month: 0,
    hourlyWork: [],
    hourlyRateNames: [],
    basicSalary: 0,
    mealEarnings: 0,
    paidByBank: 0,
    paidByHand: 0,
    totalAmountEarned: 0,
    paidOnDate: "",
    bonus: 0,
    paidExpenses: [],
    regularBreakfasts: 0,
    regularLunches: 0,
    regularDinners: 0,
    cashBreakfasts: 0,
    cashLunches: 0,
    cashDinners: 0,
    vacationDays: 0,
    sickLeaveDays: 0,
    travelExpenses: 0,
    correction: 0,
    effectiveKilometers: 0,
    effectiveHours: 0,
    note: ""
  },
  carnets: [],
  totals: {
    hoursTotals: [],
    effectiveKilometers: 0,
    effectiveHours: 0,
    regularBreakfastsTotal: 0,
    regularLunchesTotal: 0,
    regularDinnersTotal: 0,
    cashBreakfastsTotal: 0,
    cashLunchesTotal: 0,
    cashDinnersTotal: 0,
    sickLeavesTotal: 0,
    vacationsTotal: 0,
  },
  loading: false,
  error: undefined,
  calculationPrintModalOpen: false,
  calculationPopoverOpen: { isOpen: false },
  calculationAllPayrollsConfirmationOpen: false,
  indexForDeletion: -1,
  carnetsLoading: false,
  calculatePayrollPopover: {
    isOpen: false,
    top: 0,
    left: 0
  },
  deletePayrollModalOpen: false,
};

export const payrollSlice = createSlice({
  name: "hr-salary",
  initialState: initialState,
  reducers: {
    setCalculationPopoverOpen: (state, action: PayloadAction<IPopoverState>) => {
      state.calculationPopoverOpen = action.payload;
    },
    setCalculationAllPayrollsConfirmationOpen: (state, action: PayloadAction<boolean>) => {
      state.calculationAllPayrollsConfirmationOpen = action.payload;
    },
    setCalculationPrintModalOpen: (state, action: PayloadAction<boolean>) => {
      state.calculationPrintModalOpen = action.payload;
    },
    closeCalculationModals: (state) => {
      state.calculationPrintModalOpen = false;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setCalculatePayrollPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.calculatePayrollPopover = action.payload;
    },
    setDeletePayrollModalOpen: (
      state,
      action: PayloadAction<{ visible: boolean; index: number }>
    ) => {
      state.deletePayrollModalOpen = action.payload.visible;
      state.indexForDeletion = action.payload.index;
    },
    updatePayrollForPrint: (state, action: PayloadAction<IUpdatePayrollForPrintParams>) => {
      const data = action.payload;
      const index = state.payrollsForPrint.findIndex(
        (p) => p.year === data.year && p.month === data.month
      );

      if (index === -1) {
        console.warn(`Payroll not found for ${data.year}-${data.month}`);
        return;
      }

      const updates = Object.fromEntries(
        Object.entries(data).filter(([_, value]) => value !== undefined)
      );

      state.payrollsForPrint[index] = {
        ...state.payrollsForPrint[index],
        ...updates
      };
    },
    deletePayrollForPrint: (state, action: PayloadAction<{
      year: number, month: number
    }>) => {
      const { year, month } = action.payload;
      state.payrollsForPrint = state.payrollsForPrint.filter(
        (p) => !(p.year === year && p.month === month)
      );
    },
    updatePayroll: (state, action: PayloadAction<IPayroll>) => {
      const payroll = action.payload;
      let index = state.payrolls.findIndex(
        (p) => p.payrollId === payroll.payrollId
      );
      state.payrolls[index] = payroll;
    },
    updateExpense: (
      state,
      action: PayloadAction<{ payrollId: number; expense: IExpense }>
    ) => {
      const { payrollId, expense } = action.payload;
      const payrollIndex = state.payrolls.findIndex(
        (p) => p.payrollId === payrollId
      );
      const expenseIndex = state.payrolls[payrollIndex].expenses.findIndex(
        (e) => e.expenseId === expense.expenseId
      );
      state.payrolls[payrollIndex].expenses[expenseIndex] = expense;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getAllPayrollsForEmployee.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getAllPayrollsForEmployee.fulfilled, (state, action) => {
      state.loading = false;
      state.payrolls = action.payload ? action.payload : [];
    });
    builder.addCase(getAllPayrollsForEmployee.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getAllPayrollsForEmployeePrint.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getAllPayrollsForEmployeePrint.fulfilled, (state, action) => {
      state.loading = false;
      state.payrollsForPrint = action.payload ? action.payload.payrolls : [];
    });
    builder.addCase(getAllPayrollsForEmployeePrint.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getMonthlyPayrollForEmployeePrint.pending, (state, action) => {
      state.loading = true;
    });
    builder.addCase(getMonthlyPayrollForEmployeePrint.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.payrollForPrint = action.payload.payrolls[0];
      }
    });
    builder.addCase(getMonthlyPayrollForEmployeePrint.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(getMonthlyCarnetsForEmployee.pending, (state) => {
      state.carnetsLoading = true;
    });
    builder.addCase(getMonthlyCarnetsForEmployee.fulfilled, (state, action) => {
      state.carnets = action.payload ? action.payload.carnets : [];
      state.totals.hoursTotals = action.payload
        ? action.payload.hoursTotals
        : [];
      state.totals.effectiveKilometers = action.payload
        ? action.payload.effectiveKilometers
        : 0;
      state.totals.effectiveHours = action.payload
        ? action.payload.effectiveHours
        : 0;
      state.totals.regularBreakfastsTotal = action.payload
        ? action.payload.regularBreakfastsTotal
        : 0;
      state.totals.regularLunchesTotal = action.payload
        ? action.payload.regularLunchesTotal
        : 0;
      state.totals.regularDinnersTotal = action.payload
        ? action.payload.regularDinnersTotal
        : 0;
      state.totals.cashBreakfastsTotal = action.payload
        ? action.payload.cashBreakfastsTotal
        : 0;
      state.totals.cashLunchesTotal = action.payload
        ? action.payload.cashLunchesTotal
        : 0;
      state.totals.cashDinnersTotal = action.payload
        ? action.payload.cashDinnersTotal
        : 0;
      state.totals.sickLeavesTotal = action.payload
        ? action.payload.sickLeavesTotal
        : 0;
      state.totals.vacationsTotal = action.payload
        ? action.payload.vacationsTotal
        : 0;
      state.carnetsLoading = false;
    });
    builder.addCase(getMonthlyCarnetsForEmployee.rejected, (state, action) => {
      state.error = action.error;
      state.carnetsLoading = false;
    });
    builder.addCase(deletePayroll.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deletePayroll.fulfilled, (state, action) => {
      state.loading = false;
      state.payrolls = state.payrolls.filter(payroll =>
        payroll.payrollId !== action.payload?.params.payrollId);
    });
    builder.addCase(deletePayroll.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
  },
});

export const {
  setCalculationPopoverOpen,
  setCalculationAllPayrollsConfirmationOpen,
  setCalculationPrintModalOpen,
  closeCalculationModals,
  setError,
  setCalculatePayrollPopover,
  setDeletePayrollModalOpen,
  updatePayroll,
  updatePayrollForPrint,
  deletePayrollForPrint,
  updateExpense,
} = payrollSlice.actions;

export const selectState = (state: RootState) => state.hr.payroll;
export const selectPayrollForDeletion = (state: RootState) =>
  state.hr.payroll.payrolls[state.hr.payroll.indexForDeletion];
export default payrollSlice.reducer;
